import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'
import axios from '../api/client'
import QRCode from 'qrcode'
import { Button, IconButton, Select, Input, Badge } from '../components/common'
import { EngagementDashboard } from '../components/admin/EngagementDashboard'
import { TOUR_DEBUG_KEY } from '../components/Tour/Tour'
import styles from './AdminPage.module.css'
import pageStyles from './PageStyles.module.css'

interface ImageFetcherStatus {
  running: boolean
  phase: 'artists' | 'labels' | 'idle'
  processed: number
  remaining: number
  found: number
  errors: number
  startedAt: string | null
}

interface PipelineWorkerStatus {
  workers: Array<{
    worker_id: string
    hostname: string | null
    version: string | null
    phase: string | null
    current_job: string | null
    last_error: string | null
    started_at: string | null
    last_seen_at: string
    metrics: Record<string, number | string>
  }>
  queue: Array<{ status: string; count: number }>
  dirty: Array<{ kind: string; status: string; count: number; oldest_at: string | null }>
  maintenance: Array<{
    name: string
    status: string
    dirty_count: number
    last_completed_at: string | null
    last_error: string | null
  }>
  sync: {
    tracked: number
    stale_30d: number
    oldest_metadata_at: string | null
    newest_metadata_at: string | null
  }
  disk: { totalBytes: number; freeBytes: number } | null
  serverTime: string
  targetModel: string
}

interface InviteCode {
  id: number
  code: string
  used_by: number | null
  used_by_email: string | null
  used_at: string | null
  created_at: string
}

interface UserRecord {
  id: number
  email: string
  role: string
  created_at: string
}

interface ActivityRecord {
  id: number
  user_id: number
  user_email: string
  action: string
  metadata: string | null
  ip: string | null
  created_at: string
}

function relativeTime(dateStr: string): string {
  const normalized = dateStr.includes('T') ? dateStr : `${dateStr.replace(' ', 'T')}Z`
  const diff = Date.now() - new Date(normalized).getTime()
  if (!Number.isFinite(diff)) return dateStr
  const secs = Math.floor(diff / 1000)
  if (secs < 60) return 'just now'
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`
  if (secs < 7 * 86400) return `${Math.floor(secs / 86400)}d ago`
  return new Date(dateStr).toLocaleDateString()
}

export function AdminPage() {
  const { isAdmin } = useAuth()
  const navigate = useNavigate()
  const [invites, setInvites] = useState<InviteCode[]>([])
  const [users, setUsers] = useState<UserRecord[]>([])
  const [activity, setActivity] = useState<ActivityRecord[]>([])
  const [newCode, setNewCode] = useState<string | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [copied, setCopied] = useState(false)
  const [userSearch, setUserSearch] = useState('')
  const [copiedEmails, setCopiedEmails] = useState(false)

  // Discogs OAuth settings
  const [consumerKey, setConsumerKey] = useState('')
  const [consumerSecret, setConsumerSecret] = useState('')
  const [isSavingDiscogs, setIsSavingDiscogs] = useState(false)
  const [discogsSaved, setDiscogsSaved] = useState(false)

  // Telegram bot settings
  const [telegramToken, setTelegramToken] = useState('')
  const [isSavingTelegram, setIsSavingTelegram] = useState(false)
  const [telegramSaved, setTelegramSaved] = useState(false)
  const [telegramIsSet, setTelegramIsSet] = useState(false)

  // Open registration
  const [tourDebug, setTourDebug] = useState(() => {
    try { return localStorage.getItem(TOUR_DEBUG_KEY) === '1' } catch { return false }
  })

  const toggleTourDebug = () => {
    const next = !tourDebug
    try {
      if (next) localStorage.setItem(TOUR_DEBUG_KEY, '1')
      else localStorage.removeItem(TOUR_DEBUG_KEY)
    } catch { /* ignore */ }
    setTourDebug(next)
    if (next) {
      try { localStorage.removeItem('tenwords:tour-index') } catch { /* ignore */ }
      try { localStorage.removeItem('tenwords:tour-state-v2') } catch { /* ignore */ }
    }
  }

  const [openReg, setOpenReg] = useState<{ active: false } | { active: true; token: string; expiresAt: number }>({ active: false })
  const [openRegDuration, setOpenRegDuration] = useState(60)
  const [openRegLoading, setOpenRegLoading] = useState(false)
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)
  const [projectorMode, setProjectorMode] = useState(false)
  const [countdown, setCountdown] = useState('')
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Image fetcher
  const [fetcherStatus, setFetcherStatus] = useState<ImageFetcherStatus | null>(null)
  const [fetcherLoading, setFetcherLoading] = useState(false)
  const [pipelineStatus, setPipelineStatus] = useState<PipelineWorkerStatus | null>(null)
  const [pipelineError, setPipelineError] = useState('')

  useEffect(() => {
    if (!isAdmin) { navigate('/releases'); return }
    fetchData()
    loadFetcherStatus()
    loadPipelineStatus()
    loadOpenRegStatus()
  }, [isAdmin])

  // Poll fetcher status every 5s while running
  useEffect(() => {
    if (!fetcherStatus?.running) return
    const id = setInterval(loadFetcherStatus, 5000)
    return () => clearInterval(id)
  }, [fetcherStatus?.running])

  // This status request reads only small worker/queue tables; it never launches
  // maintenance or counts the large neighbour tables.
  useEffect(() => {
    if (!isAdmin) return
    const id = setInterval(loadPipelineStatus, 10_000)
    return () => clearInterval(id)
  }, [isAdmin])

  // Countdown ticker for open registration
  useEffect(() => {
    if (countdownRef.current) clearInterval(countdownRef.current)
    if (!openReg.active) { setCountdown(''); return }
    const tick = () => {
      const ms = openReg.expiresAt - Date.now()
      if (ms <= 0) { setCountdown('Expired'); setOpenReg({ active: false }); return }
      const m = Math.floor(ms / 60000)
      const s = Math.floor((ms % 60000) / 1000)
      setCountdown(`${m}:${String(s).padStart(2, '0')}`)
    }
    tick()
    countdownRef.current = setInterval(tick, 1000)
    return () => { if (countdownRef.current) clearInterval(countdownRef.current) }
  }, [openReg])

  const loadOpenRegStatus = async () => {
    const { data } = await axios.get('/users/open-reg/status')
    if (data.active) {
      setOpenReg({ active: true, token: data.token, expiresAt: data.expiresAt })
      generateQr(data.token)
    } else {
      setOpenReg({ active: false })
    }
  }

  const generateQr = async (token: string) => {
    const url = `${window.location.origin}/login?open_token=${token}&tab=register`
    const dataUrl = await QRCode.toDataURL(url, { width: 400, margin: 2, color: { dark: '#000000', light: '#ffffff' } })
    setQrDataUrl(dataUrl)
  }

  const activateOpenReg = async () => {
    setOpenRegLoading(true)
    try {
      const { data } = await axios.post('/users/open-reg', { minutes: openRegDuration })
      setOpenReg({ active: true, token: data.token, expiresAt: data.expiresAt })
      await generateQr(data.token)
    } finally {
      setOpenRegLoading(false)
    }
  }

  const revokeOpenReg = async () => {
    await axios.delete('/users/open-reg')
    setOpenReg({ active: false })
    setQrDataUrl(null)
    setProjectorMode(false)
  }

  const loadFetcherStatus = async () => {
    const { data } = await axios.get('/image-fetcher/status')
    if (data.success) setFetcherStatus(data.status)
  }

  const loadPipelineStatus = async () => {
    try {
      const { data } = await axios.get('/worker/admin/status')
      if (data.success) setPipelineStatus(data.status)
      setPipelineError('')
    } catch (error) {
      setPipelineError(error instanceof Error ? error.message : 'Could not load worker status')
    }
  }

  const startFetcher = async (phase: 'artists' | 'labels' | 'both') => {
    setFetcherLoading(true)
    try {
      await axios.post('/image-fetcher/start', { phase })
      await loadFetcherStatus()
    } finally {
      setFetcherLoading(false)
    }
  }

  const stopFetcher = async () => {
    setFetcherLoading(true)
    try {
      await axios.post('/image-fetcher/stop')
      await loadFetcherStatus()
    } finally {
      setFetcherLoading(false)
    }
  }

  const fetchData = async () => {
    const [invRes, userRes, settingsRes, activityRes] = await Promise.all([
      axios.get('/users/invites'),
      axios.get('/users/list'),
      axios.get('/users/settings'),
      axios.get('/users/activity'),
    ])
    setInvites(invRes.data.invites || [])
    setUsers(userRes.data.users || [])
    setActivity(activityRes.data.activities || [])
    const s = settingsRes.data.settings || {}
    setConsumerKey(s.discogs_consumer_key || '')
    setConsumerSecret(s.discogs_consumer_secret === '••••••••' ? '' : '')
    setTelegramIsSet(s.telegram_bot_token === '••••••••')
    setTelegramToken('')
  }

  const saveDiscogsSettings = async () => {
    setIsSavingDiscogs(true)
    try {
      const payload: Record<string, string> = { discogs_consumer_key: consumerKey }
      if (consumerSecret) payload.discogs_consumer_secret = consumerSecret
      await axios.put('/users/settings', payload)
      setDiscogsSaved(true)
      setTimeout(() => setDiscogsSaved(false), 2500)
      await fetchData()
    } finally {
      setIsSavingDiscogs(false)
    }
  }

  const saveTelegramSettings = async () => {
    if (!telegramToken) return
    setIsSavingTelegram(true)
    try {
      await axios.put('/users/settings', { telegram_bot_token: telegramToken })
      setTelegramSaved(true)
      setTimeout(() => setTelegramSaved(false), 2500)
      await fetchData()
    } finally {
      setIsSavingTelegram(false)
    }
  }

  const generateCode = async () => {
    setIsGenerating(true)
    setNewCode(null)
    try {
      const { data } = await axios.post('/users/invite')
      setNewCode(data.code)
      await fetchData()
    } finally {
      setIsGenerating(false)
    }
  }

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const filteredUsers = users.filter(u =>
    u.email.toLowerCase().includes(userSearch.toLowerCase().trim())
  )

  const copyAllEmails = () => {
    navigator.clipboard.writeText(filteredUsers.map(u => u.email).join(', '))
    setCopiedEmails(true)
    setTimeout(() => setCopiedEmails(false), 2000)
  }

  const fetcherTotal = fetcherStatus ? fetcherStatus.processed + fetcherStatus.remaining : 0
  const fetcherPct = fetcherTotal > 0 ? Math.round((fetcherStatus!.processed / fetcherTotal) * 100) : 0
  const primaryWorker = pipelineStatus?.workers[0]
  const workerOnline = primaryWorker
    ? Date.now() - new Date(primaryWorker.last_seen_at).getTime() < 10 * 60_000
    : false
  const queueCount = (status: string) => pipelineStatus?.queue.find(row => row.status === status)?.count || 0
  const dirtyCount = (kind: string) => pipelineStatus?.dirty
    .filter(row => row.kind === kind && row.status === 'queued')
    .reduce((sum, row) => sum + row.count, 0) || 0
  const workerMetric = (name: string) => Number(primaryWorker?.metrics[name] || 0)
  const formatBytes = (bytes: number) => `${(bytes / 1024 ** 3).toFixed(1)} GB`

  return (
    <div className={pageStyles.page}>
      <div className={pageStyles.pageHeader}>
        <h1>Admin</h1>
      </div>

      <div className={styles.content}>
        {/* Pi → Hetzner catalogue and audio pipeline */}
        <section id="catalogue-audio-pipeline" className={styles.section}>
          <h2 className={styles.sectionTitle}>Catalogue + Audio Pipeline</h2>
          <p className={styles.sectionDesc}>
            The Pi refreshes Discogs data, discovers Electronic vinyl and performs all YouTube/audio analysis. Hetzner stores compact serving data only.
          </p>
          {pipelineStatus ? (
            <div className={styles.pipelineCard}>
              <div className={styles.fetcherHeader}>
                <span className={`${styles.fetcherStatus} ${workerOnline ? styles.fetcherRunning : styles.pipelineOffline}`}>
                  <i className={`bi ${workerOnline ? 'bi-broadcast' : 'bi-exclamation-triangle'}`}></i>
                  {workerOnline ? `Pi online — ${primaryWorker?.phase || 'idle'}` : 'Pi offline or heartbeat stale'}
                </span>
                <Button variant="secondary" size="sm" icon="bi bi-arrow-clockwise" onClick={loadPipelineStatus}>
                  Refresh
                </Button>
              </div>

              <div className={styles.pipelineMeta}>
                <span>{primaryWorker ? `${primaryWorker.worker_id} · ${relativeTime(primaryWorker.last_seen_at)}` : 'No worker has connected yet'}</span>
                <span>Model: {pipelineStatus.targetModel}</span>
                {primaryWorker?.current_job && <span>Current: {primaryWorker.current_job}</span>}
              </div>

              <div className={styles.pipelineGrid}>
                <div className={styles.pipelineLane}>
                  <span className={styles.pipelineLaneTitle}>Audio jobs</span>
                  <strong>{queueCount('queued').toLocaleString()}</strong>
                  <span>{queueCount('processing')} active · {queueCount('retry')} retry · {queueCount('blocked')} blocked · {workerMetric('audio_completed').toLocaleString()} completed</span>
                </div>
                <div className={styles.pipelineLane}>
                  <span className={styles.pipelineLaneTitle}>Discogs freshness</span>
                  <strong>{(pipelineStatus.sync.tracked || 0).toLocaleString()}</strong>
                  <span>{(pipelineStatus.sync.stale_30d || 0).toLocaleString()} stale · {workerMetric('releases_refreshed').toLocaleString()} refreshed · {workerMetric('releases_discovered').toLocaleString()} new</span>
                </div>
                <div className={styles.pipelineLane}>
                  <span className={styles.pipelineLaneTitle}>Audio neighbours</span>
                  <strong>{dirtyCount('audio_neighbors').toLocaleString()}</strong>
                  <span>waiting for a bounded derived batch</span>
                </div>
                <div className={styles.pipelineLane}>
                  <span className={styles.pipelineLaneTitle}>Metadata neighbours</span>
                  <strong>{dirtyCount('release_neighbors').toLocaleString()}</strong>
                  <span>dirty releases</span>
                </div>
                <div className={styles.pipelineLane}>
                  <span className={styles.pipelineLaneTitle}>Map + indexes</span>
                  <strong>{dirtyCount('map').toLocaleString()}</strong>
                  <span>positions waiting · indexes update per row</span>
                </div>
                <div className={styles.pipelineLane}>
                  <span className={styles.pipelineLaneTitle}>Masters + entities</span>
                  <strong>{(dirtyCount('canonical') + dirtyCount('entities')).toLocaleString()}</strong>
                  <span>incremental aggregate work</span>
                </div>
              </div>

              {pipelineStatus.maintenance.length > 0 && (
                <div className={styles.pipelineMaintenance}>
                  {pipelineStatus.maintenance.map(item => (
                    <span key={item.name} className={item.last_error ? styles.pipelineWarning : ''}>
                      {item.name}: {item.status} ({item.dirty_count || 0})
                    </span>
                  ))}
                </div>
              )}
              {pipelineStatus.disk && (
                <p className={styles.fetcherMeta}>
                  Hetzner disk: {formatBytes(pipelineStatus.disk.freeBytes)} free of {formatBytes(pipelineStatus.disk.totalBytes)}
                </p>
              )}
              {primaryWorker?.last_error && <p className={styles.pipelineError}>{primaryWorker.last_error}</p>}
            </div>
          ) : (
            <p className={styles.muted}>{pipelineError || 'Loading…'}</p>
          )}
        </section>

        {/* Engagement dashboard (replaces the old flat activity table) */}
        <EngagementDashboard activity={activity} />

        {/* Image Fetcher */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Image Fetcher</h2>
          {fetcherStatus ? (
            <div className={styles.fetcherCard}>
              <div className={styles.fetcherHeader}>
                <span className={`${styles.fetcherStatus} ${fetcherStatus.running ? styles.fetcherRunning : styles.fetcherIdle}`}>
                  <i className={`bi ${fetcherStatus.running ? 'bi-arrow-repeat' : 'bi-pause-circle'}`}></i>
                  {fetcherStatus.running ? `Running — ${fetcherStatus.phase}` : 'Idle'}
                </span>
                {fetcherStatus.running ? (
                  <Button variant="secondary" icon="bi bi-stop-circle" onClick={stopFetcher} disabled={fetcherLoading}>
                    Stop
                  </Button>
                ) : (
                  <div className={styles.fetcherActions}>
                    <Button variant="secondary" icon="bi bi-play-circle" onClick={() => startFetcher('both')} disabled={fetcherLoading}>
                      Start (artists + labels)
                    </Button>
                    <Button variant="secondary" onClick={() => startFetcher('artists')} disabled={fetcherLoading}>
                      Artists only
                    </Button>
                    <Button variant="secondary" onClick={() => startFetcher('labels')} disabled={fetcherLoading}>
                      Labels only
                    </Button>
                  </div>
                )}
              </div>
              <div className={styles.fetcherStats}>
                <div className={styles.fetcherStat}>
                  <span className={styles.fetcherStatValue}>{fetcherStatus.processed.toLocaleString()}</span>
                  <span className={styles.fetcherStatLabel}>processed</span>
                </div>
                <div className={styles.fetcherStat}>
                  <span className={styles.fetcherStatValue}>{fetcherStatus.found.toLocaleString()}</span>
                  <span className={styles.fetcherStatLabel}>images found</span>
                </div>
                <div className={styles.fetcherStat}>
                  <span className={styles.fetcherStatValue}>{fetcherStatus.remaining.toLocaleString()}</span>
                  <span className={styles.fetcherStatLabel}>remaining</span>
                </div>
                <div className={styles.fetcherStat}>
                  <span className={styles.fetcherStatValue}>{fetcherStatus.errors.toLocaleString()}</span>
                  <span className={styles.fetcherStatLabel}>errors</span>
                </div>
              </div>
              {fetcherTotal > 0 && (
                <div className={styles.fetcherProgressWrap}>
                  <div className={styles.fetcherProgressBar}>
                    <div className={styles.fetcherProgressFill} style={{ width: `${fetcherPct}%` }} />
                  </div>
                  <span className={styles.fetcherProgressPct}>{fetcherPct}%</span>
                </div>
              )}
              {fetcherStatus.startedAt && (
                <p className={styles.fetcherMeta}>Started {relativeTime(fetcherStatus.startedAt)}</p>
              )}
            </div>
          ) : (
            <p className={styles.muted}>Loading…</p>
          )}
        </section>

        {/* Open Registration QR */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Open Registration</h2>
          <p className={styles.sectionDesc}>
            Generate a time-limited registration link. Anyone with the QR code can create an account without an invite code.
          </p>

          {!openReg.active ? (
            <div className={styles.openRegControls}>
              <Select
                className={styles.durationSelect}
                value={openRegDuration}
                onChange={e => setOpenRegDuration(Number((e.target as HTMLSelectElement).value))}
                options={[
                  { value: 30, label: '30 minutes' },
                  { value: 60, label: '1 hour' },
                  { value: 120, label: '2 hours' },
                  { value: 240, label: '4 hours' },
                ]}
              />
              <Button variant="secondary" icon="bi bi-qr-code" onClick={activateOpenReg} disabled={openRegLoading}>
                {openRegLoading ? 'Generating…' : 'Generate QR code'}
              </Button>
            </div>
          ) : (
            <div className={styles.openRegActive}>
              <div className={styles.openRegHeader}>
                <span className={styles.openRegLive}>
                  <span className={styles.liveDot} /> Open — expires in {countdown}
                </span>
                <div className={styles.openRegActions}>
                  <Button variant="secondary" icon="bi bi-fullscreen" onClick={() => setProjectorMode(true)}>
                    Projector view
                  </Button>
                  <Button variant="danger" icon="bi bi-x-circle" onClick={revokeOpenReg}>
                    Revoke
                  </Button>
                </div>
              </div>
              {qrDataUrl && (
                <div className={styles.qrWrap}>
                  <img src={qrDataUrl} alt="Registration QR code" className={styles.qrImg} />
                  <p className={styles.qrUrl}>
                    {`${window.location.origin}/login?open_token=${openReg.token}&tab=register`}
                  </p>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Generate invite */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Invite Codes</h2>
          <div className={styles.generateRow}>
            <Button variant="secondary" icon="bi bi-plus-circle" onClick={generateCode} disabled={isGenerating}>
              {isGenerating ? 'Generating…' : 'Generate invite code'}
            </Button>
            {newCode && (
              <div className={styles.newCode}>
                <span className={styles.codeText}>{newCode}</span>
                <Button variant="secondary" size="sm" icon={`bi ${copied ? 'bi-check' : 'bi-clipboard'}`} onClick={() => copyCode(newCode)}>
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
            )}
          </div>

          <table className={styles.table}>
            <thead>
              <tr>
                <th>Code</th>
                <th>Status</th>
                <th>Used by</th>
                <th>Used at</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {invites.length === 0 ? (
                <tr><td colSpan={5} className={styles.empty}>No invite codes yet</td></tr>
              ) : invites.map(inv => (
                <tr key={inv.id}>
                  <td>
                    <code className={styles.code}>{inv.code}</code>
                    {!inv.used_by && (
                      <IconButton variant="ghost" size="sm" icon="bi bi-clipboard" aria-label="Copy" title="Copy" onClick={() => copyCode(inv.code)} />
                    )}
                  </td>
                  <td>
                    <Badge tone={inv.used_by ? 'neutral' : 'success'}>
                      {inv.used_by ? 'Used' : 'Available'}
                    </Badge>
                  </td>
                  <td className={styles.muted}>{inv.used_by_email || '—'}</td>
                  <td className={styles.muted}>{inv.used_at ? new Date(inv.used_at).toLocaleDateString() : '—'}</td>
                  <td className={styles.muted}>{new Date(inv.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* Discogs OAuth Configuration */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Discogs OAuth</h2>
          <p className={styles.sectionDesc}>
            Register your app at <a href="https://www.discogs.com/settings/developers" target="_blank" rel="noreferrer">discogs.com/settings/developers</a> to get a consumer key and secret. Users can then connect their Discogs accounts via OAuth in Settings.
          </p>
          <div className={styles.settingsForm}>
            <div className={styles.field}>
              <label className={styles.label}>Consumer Key</label>
              <Input
                type="text"
                value={consumerKey}
                onChange={e => setConsumerKey(e.target.value)}
                placeholder="Your Discogs consumer key"
                autoComplete="off"
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Consumer Secret</label>
              <Input
                type="password"
                value={consumerSecret}
                onChange={e => setConsumerSecret(e.target.value)}
                placeholder="Leave blank to keep existing secret"
                autoComplete="new-password"
              />
            </div>
            <Button
              variant="secondary"
              icon={`bi ${discogsSaved ? 'bi-check' : 'bi-floppy'}`}
              onClick={saveDiscogsSettings}
              disabled={isSavingDiscogs || !consumerKey}
            >
              {discogsSaved ? 'Saved!' : isSavingDiscogs ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </section>

        {/* Telegram Bot Configuration */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Telegram Bot</h2>
          <p className={styles.sectionDesc}>
            Create a bot via <b>@BotFather</b> on Telegram and paste the token here. Users can then link their Telegram chat ID in Settings to receive watchlist alerts.
            {telegramIsSet && <span style={{ color: 'var(--stonks-accent-primary)', marginLeft: 8 }}>✓ Token is set</span>}
          </p>
          <div className={styles.settingsForm}>
            <div className={styles.field}>
              <label className={styles.label}>Bot Token</label>
              <Input
                type="password"
                value={telegramToken}
                onChange={e => setTelegramToken(e.target.value)}
                placeholder={telegramIsSet ? 'Leave blank to keep existing token' : 'Paste bot token from @BotFather'}
                autoComplete="new-password"
              />
            </div>
            <Button
              variant="secondary"
              icon={`bi ${telegramSaved ? 'bi-check' : 'bi-floppy'}`}
              onClick={saveTelegramSettings}
              disabled={isSavingTelegram || !telegramToken}
            >
              {telegramSaved ? 'Saved!' : isSavingTelegram ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </section>

        {/* Users list */}
        <section className={styles.section}>
          <div className={styles.usersHeader}>
            <h2 className={styles.sectionTitle}>Users ({users.length})</h2>
            <div className={styles.usersTools}>
              <Input
                className={styles.userSearch}
                type="text"
                placeholder="Search email…"
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
              />
              <Button
                variant="secondary"
                size="sm"
                icon={`bi ${copiedEmails ? 'bi-check' : 'bi-clipboard'}`}
                onClick={copyAllEmails}
                disabled={filteredUsers.length === 0}
                title="Copy all listed emails (comma-separated)"
              >
                {copiedEmails ? 'Copied!' : `Copy ${filteredUsers.length} email${filteredUsers.length === 1 ? '' : 's'}`}
              </Button>
            </div>
          </div>
          <div className={styles.userTableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr><td colSpan={4} className={styles.empty}>No matching users</td></tr>
                ) : filteredUsers.map(u => (
                  <tr key={u.id}>
                    <td className={styles.muted}>{u.id}</td>
                    <td>{u.email}</td>
                    <td>
                      <Badge tone={u.role === 'admin' ? 'accent' : 'neutral'}>
                        {u.role}
                      </Badge>
                    </td>
                    <td className={styles.muted}>{new Date(u.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Tour debug */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Onboarding Tour</h2>
          <div className={styles.generateRow}>
            <div>
              <div style={{ fontSize: '0.875rem', color: 'var(--stonks-text)', fontWeight: 500, marginBottom: 4 }}>
                Debug mode
              </div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--stonks-text-muted)' }}>
                When on, the tour restarts from step 1 on every page reload. Turn off to restore normal behaviour.
              </div>
            </div>
            <Button
              variant={tourDebug ? 'primary' : 'secondary'}
              icon={`bi ${tourDebug ? 'bi-bug-fill' : 'bi-bug'}`}
              onClick={toggleTourDebug}
            >
              {tourDebug ? 'Debug on — reload to restart' : 'Enable debug'}
            </Button>
          </div>
          {tourDebug && (
            <div style={{ padding: '10px 0 0 0', fontSize: '0.8125rem', color: 'var(--stonks-accent-primary)' }}>
              <i className="bi bi-info-circle" /> Reload any page to restart the tour from step 1. Completion is not recorded while debug is active.
            </div>
          )}
        </section>
      </div>

      {/* Projector overlay */}
      {projectorMode && openReg.active && qrDataUrl && (
        <div className={styles.projectorOverlay} onClick={() => setProjectorMode(false)}>
          <div className={styles.projectorCard} onClick={e => e.stopPropagation()}>
            <div className={styles.projectorHeader}>
              <span className={styles.projectorTitle}>tenwords.de — Create an account</span>
              <div className={styles.projectorMeta}>
                <span className={styles.projectorCountdown}>Expires in {countdown}</span>
                <IconButton variant="ghost" icon="bi bi-x-lg" aria-label="Close" onClick={() => setProjectorMode(false)} className={styles.projectorClose} />
              </div>
            </div>
            <img src={qrDataUrl} alt="Registration QR code" className={styles.projectorQr} />
            <p className={styles.projectorSub}>Scan to register · No invite code needed</p>
          </div>
        </div>
      )}
    </div>
  )
}
